import { courseServiceApi } from "../../apis/courseService.api";
import { COURSE_API_TAGS } from "../../app.constants";
import type {
  CategoriesListParams,
  CategoriesListResponse,
  CourseResponse,
  CoursesHighlightsResponse,
  CourseTagResponse,
  CreateCourseDto,
  CreateCourseHighlightsDto,
  CreateCourseTagsDto,
  CreateSubcategoryDto,
  DeleteCategoryParams,
  DeleteSubcategoryParams,
  DeleteTagParams,
  ICreateAdminDto,
  IGenericResponse,
  SingleCourseResponse,
  SubcategoriesListResponse,
  TagDto,
  TagResponse,
  UpdateCategoryBodyDto,
  UpdateCategoryParams,
  UpdateCourseDto,
  UpdateCourseHighlightsDto,
  UpdateSubcategoryBodyDto,
  UpdateSubcategoryParams,
  UpdateTagParams,
} from "./admin.types";

const ADMIN_URL = "/admin";

export const adminApi = courseServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // CREATE CATEGORY
    createCategories: builder.mutation<IGenericResponse, ICreateAdminDto>({
      query: (body) => ({
        url: `${ADMIN_URL}/categories`,
        method: "POST",
        body,
      }),
      invalidatesTags: [COURSE_API_TAGS.CATEGORIES],
    }),

    // GET CATEGORIES

    getCategories: builder.query<CategoriesListResponse, CategoriesListParams>({
      query: (params) => ({
        url: `${ADMIN_URL}/categories`,
        method: "GET",
        params,
      }),
      providesTags: [COURSE_API_TAGS.CATEGORIES],
    }),

    // UPDATE CATEGORY
    updateCategories: builder.mutation<
      void,
      UpdateCategoryParams & UpdateCategoryBodyDto
    >({
      query: ({ id, ...rest }) => ({
        url: `${ADMIN_URL}/categories/${id}`,
        method: "PATCH",
        body: rest,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        COURSE_API_TAGS.CATEGORIES,
        { type: COURSE_API_TAGS.CATEGORIES, id: id },
      ],
    }),

    // DELETE CATEGORY
    deleteCategory: builder.mutation<void, DeleteCategoryParams>({
      query: ({ id }) => ({
        url: `${ADMIN_URL}/categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [
        COURSE_API_TAGS.CATEGORIES,
        { type: COURSE_API_TAGS.CATEGORIES, id },
      ],
    }),

    // CREATE SUBCATEGORY
    createSubcategory: builder.mutation<IGenericResponse, CreateSubcategoryDto>(
      {
        query: (body) => ({
          url: `${ADMIN_URL}/subcategories`,
          method: "POST",
          body,
        }),
        invalidatesTags: [COURSE_API_TAGS.SUBCATEGORIES],
      },
    ),

    // GET SUBCATEGORIES
    getSubcategories: builder.query<SubcategoriesListResponse, void>({
      query: () => ({
        url: `${ADMIN_URL}/subcategories`,
        method: "GET",
      }),
      providesTags: [COURSE_API_TAGS.SUBCATEGORIES],
    }),

    // UPDATE SUBCATEGORY
    updateSubcategory: builder.mutation<
      void,
      UpdateSubcategoryParams & UpdateSubcategoryBodyDto
    >({
      query: ({ id, ...rest }) => ({
        url: `${ADMIN_URL}/subcategories/${id}`,
        method: "PATCH",
        body: rest,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        COURSE_API_TAGS.SUBCATEGORIES,
        { type: COURSE_API_TAGS.SUBCATEGORIES, id },
      ],
    }),

    // DELETE SUBCATEGORY
    deleteSubcategory: builder.mutation<void, DeleteSubcategoryParams>({
      query: ({ id }) => ({
        url: `${ADMIN_URL}/subcategories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [
        COURSE_API_TAGS.SUBCATEGORIES,
        { type: COURSE_API_TAGS.SUBCATEGORIES, id },
      ],
    }),

    // CREATE COURSE
    createCourse: builder.mutation<CourseResponse, CreateCourseDto>({
      query: (body) => ({
        url: `${ADMIN_URL}/courses`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        COURSE_API_TAGS.COURSES,
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // GET COURSES
    getCourses: builder.query<CourseResponse[], void>({
      query: () => ({
        url: `${ADMIN_URL}/courses`,
        method: "GET",
      }),
      providesTags: [COURSE_API_TAGS.COURSES],
    }),

    // GET SINGLE COURSES
    getSingleCourse: builder.query<SingleCourseResponse, number>({
      query: (id) => ({
        url: `${ADMIN_URL}/courses/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: COURSE_API_TAGS.COURSES, id }],
    }),

    // UPDATE COURSE
    updateCourse: builder.mutation<void, { id: number } & UpdateCourseDto>({
      query: ({ id, ...rest }) => ({
        url: `${ADMIN_URL}/courses/${id}`,
        method: "PATCH",
        body: rest,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        COURSE_API_TAGS.COURSES,
        { type: COURSE_API_TAGS.COURSES, id },
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // DELETE COURSE
    deleteCourse: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `${ADMIN_URL}/courses/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [
        COURSE_API_TAGS.COURSES,
        { type: COURSE_API_TAGS.COURSES, id },
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // CREATE COURSE ALLOW USERS
    createCourseAllowUsers: builder.mutation<
      IGenericResponse,
      { id: string; body: CreateCourseDto }
    >({
      query: ({ id, body }) => ({
        url: `${ADMIN_URL}/courses/${id}/allowed-users`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        COURSE_API_TAGS.COURSES_ALLOW_USERS,
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // CREATE COURSE HIGHLIGHTS
    createCourseHighlights: builder.mutation<
      IGenericResponse,
      CreateCourseHighlightsDto
    >({
      query: (body) => ({
        url: `${ADMIN_URL}/course-highlights`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        COURSE_API_TAGS.COURSES_HIGHLIGHTS,
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // GET COURSE HIGHLIGHTS
    getCoursesHighlights: builder.query<
      CoursesHighlightsResponse[],
      { id: number | string }
    >({
      query: ({ id }) => ({
        url: `${ADMIN_URL}/courses/${id}/course-highlights`,
        method: "GET",
      }),
      providesTags: [COURSE_API_TAGS.COURSES_HIGHLIGHTS],
    }),

    // UPDATE COURSE HIGHLIGHTS
    updateCoursesHighlights: builder.mutation<
      void,
      { id: number } & UpdateCourseHighlightsDto
    >({
      query: ({ id, ...rest }) => ({
        url: `${ADMIN_URL}/course-highlights/${id}`,
        method: "PATCH",
        body: rest,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        COURSE_API_TAGS.COURSES_HIGHLIGHTS,
        { type: COURSE_API_TAGS.COURSES_HIGHLIGHTS, id },
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // DELETE COURSE HIGHLIGHTS
    deleteCoursesHighlights: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `${ADMIN_URL}/course-highlights/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [
        COURSE_API_TAGS.COURSES_HIGHLIGHTS,
        { type: COURSE_API_TAGS.COURSES_HIGHLIGHTS, id },
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // CREATE TAG
    createTag: builder.mutation<TagResponse, TagDto>({
      query: (body) => ({
        url: `${ADMIN_URL}/tags`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: COURSE_API_TAGS.TAGS, id: "LIST" },
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // GET ALL TAGS
    getTags: builder.query<TagResponse[], void>({
      query: () => ({
        url: `${ADMIN_URL}/tags`,
        method: "GET",
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((tag) => ({
                type: COURSE_API_TAGS.TAGS,
                id: tag.id,
              })),
              { type: COURSE_API_TAGS.TAGS, id: "LIST" },
            ]
          : [{ type: COURSE_API_TAGS.TAGS, id: "LIST" }],
    }),

    // UPDATE TAG
    updateTag: builder.mutation<void, UpdateTagParams & TagDto>({
      query: ({ id, ...rest }) => ({
        url: `${ADMIN_URL}/tags/${id}`,
        method: "PATCH",
        body: rest,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: COURSE_API_TAGS.TAGS, id },
        { type: COURSE_API_TAGS.TAGS, id: "LIST" },
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // DELETE TAG
    deleteTag: builder.mutation<void, DeleteTagParams>({
      query: ({ id }) => ({
        url: `${ADMIN_URL}/tags/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: COURSE_API_TAGS.TAGS, id },
        { type: COURSE_API_TAGS.TAGS, id: "LIST" },
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // CREATE COURSE TAGS
    createCourseTags: builder.mutation<
      void,
      { courseId: number; body: CreateCourseTagsDto }
    >({
      query: ({ courseId, body }) => ({
        url: `${ADMIN_URL}/courses/${courseId}/tags`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { courseId }) => [
        { type: COURSE_API_TAGS.COURSES_TAGS, id: courseId },
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),

    // GET COURSE TAGS
    getCourseTags: builder.query<CourseTagResponse[], { courseId: number }>({
      query: ({ courseId }) => ({
        url: `${ADMIN_URL}/courses/${courseId}/tags`,
        method: "GET",
      }),
      providesTags: (_r, _e, { courseId }) => [
        { type: COURSE_API_TAGS.COURSES_TAGS, id: courseId },
        { type: COURSE_API_TAGS.COURSES, id: `Details` },
      ],
    }),
  }),
});

export const {
  // Category Mutation
  useCreateCategoriesMutation,
  useUpdateCategoriesMutation,
  useDeleteCategoryMutation,
  //  Category Query
  useGetCategoriesQuery,
  // Sub Category Mutations
  useCreateSubcategoryMutation,
  useUpdateSubcategoryMutation,
  useDeleteSubcategoryMutation,
  // Sub Category Queries
  useGetSubcategoriesQuery,
  // Courses Mutations
  useCreateCourseMutation,
  useUpdateCourseMutation,
  useDeleteCourseMutation,
  useCreateCourseAllowUsersMutation,
  // Courses Query
  useGetCoursesQuery,
  useGetSingleCourseQuery,
  // Courses Highlights Mutation
  useCreateCourseHighlightsMutation,
  useUpdateCoursesHighlightsMutation,
  useDeleteCoursesHighlightsMutation,
  // Courses Highlights Query
  useGetCoursesHighlightsQuery,
  // Tags Mutation
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  // Tags Query
  useGetTagsQuery,
  // Courses Tags Mutation
  useCreateCourseTagsMutation,
  // Courses Tags Query
  useGetCourseTagsQuery,
} = adminApi;
